
import { Injectable } from '@angular/core';
import { Router } from '@angular/router'
import { BehaviorSubject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { IAuthUser, UserRolesEnum } from './shared.interface';
import * as CryptoJS from 'crypto-js';
import { AES_SECRET_KEY } from 'src/app/shared/shared.const';

declare var jsPanel: any;
@Injectable({
    providedIn: 'root'
})
export class SharedService {
    authDetail: BehaviorSubject<IAuthUser | null> = new BehaviorSubject(null);
    busySpinner: BehaviorSubject<boolean> = new BehaviorSubject(false);
    jsPanelSpinner: BehaviorSubject<boolean> = new BehaviorSubject(false);
    pwaPrompt: BehaviorSubject<any> = new BehaviorSubject(null);

    get environment() {
        return environment;
    }

    get UserDetail() {
        return this.getUserDetail();
    }

    get UserId() {
        return this.UserDetail?.UserId;
    }

    get UserName() {
        return this.UserDetail?.UserName;
    }

    get Role() {
        return this.UserDetail?.Role;
    }

    get AdminPasswordReset() {
        return this.UserDetail?.AdminPasswordReset;
    }

    get AccessToken() {
        return this.UserDetail?.AccessToken;
    }

    get RefreshToken() {
        return this.UserDetail?.RefreshToken;
    }

    get AnonymousStudy() {
        return this.UserDetail?.AnonymousStudy;
    }

    constructor(
        private _http: HttpClient,
        private _router: Router) {

        this.authDetail.next(null);
    }

    fetchAuthDetail() {
        return this._http.get<any>(`${environment.APIUrl}/api/user`);
    }

    deleteUser(userId) {
        return this._http.delete<any>(`${environment.APIUrl}/api/user/${userId}`);
    }

    restoreUser(userId) {
        return this._http.patch<any>(`${environment.APIUrl}/api/user/restore/${userId}`, null);
    }

    resetUserPassword(userId, payload) {
        return this._http.post<any>(`${environment.APIUrl}/api/user/password/reset/${userId}`, payload);
    }

    getStudyList() {
        return this._http.get<any>(`${environment.APIUrl}/api/static/study`);
    }

    getSiteList() {
        return this._http.get<any>(`${environment.APIUrl}/api/static/site`);
    }

    getStudyListBySiteId(siteId) {
        return this._http.get<any>(`${environment.APIUrl}/api/static/site/studies/${siteId}`);
    }

    getPushNotificationKey() {
        return this._http.get<any>(`${environment.APIUrl}/api/public/push-notification-key`);
    }

    subscribePushNotification(userId: any, payload: any) {
        return this._http.post<any>(`${environment.APIUrl}/api/public/push-notification/subscribe/${userId}`, payload);
    }

    unSubscribePushNotification(payload: any) {
        return this._http.post<any>(`${environment.APIUrl}/api/public/push-notification/unsubscribe`, payload);
    }

    saveUserActivity(payload) {
        return this._http.post<any>(`${environment.APIUrl}/api/user/activity`, payload);
    }

    getUserActivityList(userId) {
        return this._http.get<any>(`${environment.APIUrl}/api/user/activity/list/${userId}`);
    }

    getQuestionnaireJSON(questId) {
        var time = new Date().getTime();
        return this._http.get<any>(`/json/chatbot/${questId}/questionnaire.json?time=${time}`);
    }

    getScriptJSON(questId) {
        var time = new Date().getTime();
        return this._http.get<any>(`/json/chatbot/${questId}/script.json?time=${time}`);
    }

    logout() {
        this.removeCookies();
    }

    setAuthDetail(data) {
        this.setUserDetail(data);
        this.authDetail.next(data);
    }

    getAuthDetail() {
        return this.authDetail.value;
    }

    generatePassword(length = 8) {
        var charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@$_!^",
            retVal = "";
        for (var i = 0, n = charset.length; i < length; ++i) {
            retVal += charset.charAt(Math.floor(Math.random() * n));
        }
        return retVal;
    }

    removeCookies() {
        localStorage.removeItem(environment.localStorageKey);
        this.authDetail.next(null);
    }


    getUserDetail() {
        let user: any = localStorage.getItem(environment.localStorageKey);
        if (user && user.length > 10) {
            user = this.decryptUsingAES256(user);
            try {
                user = JSON.parse(user);
            }
            catch (err) {
                user = null;
            }
        } else {
            user = null;
        }
        return user as IAuthUser;
    }

    setUserDetail(data) {
        data = JSON.stringify(data);
        data = this.encryptUsingAES256(data);
        localStorage.setItem(environment.localStorageKey, data);
    }

    navigateToLogin(loginUrl) {
        this.removeCookies();
        this.navigateToUrl(loginUrl);
    }

    closeJSPanel() {
        let panels = jsPanel.getPanels();
        panels.forEach(panel => {
            panel.close();
        })
    }

    navigateToUrl(url, querParam?) {
        this.closeJSPanel();
        this._router.navigate([url], {
            queryParams: {
                returnUrl: querParam
            }
        });
    }


    isUserHaveAccess(passedRole: string[]) {
        let isValid: boolean | null = null;
        if (this.UserDetail) {
            if (passedRole.length) {
                isValid = passedRole.indexOf(`${this.UserDetail.Role}`) > -1;
            }
        }
        return isValid;
    }

    encryptUsingAES256(value) {
        let _key = CryptoJS.enc.Utf8.parse(AES_SECRET_KEY);
        let _iv = CryptoJS.enc.Utf8.parse(AES_SECRET_KEY);
        return CryptoJS.AES.encrypt(
            value, _key, {
            keySize: AES_SECRET_KEY.length,
            iv: _iv,
            mode: CryptoJS.mode.ECB,
            padding: CryptoJS.pad.Pkcs7
        }).toString();
    }

    decryptUsingAES256(value) {
        let _key = CryptoJS.enc.Utf8.parse(AES_SECRET_KEY);
        let _iv = CryptoJS.enc.Utf8.parse(AES_SECRET_KEY);

        return CryptoJS.AES.decrypt(
            value, _key, {
            keySize: AES_SECRET_KEY.length,
            iv: _iv,
            mode: CryptoJS.mode.ECB,
            padding: CryptoJS.pad.Pkcs7
        }).toString(CryptoJS.enc.Utf8);
    }

    getObjectSortedArray(value) {
        var firstKey = "";
        var newObj = {};
        Object.keys(value).map((key, index) => {
            var id = key.substring(key.indexOf(".") + 1, key.length);
            if (parseInt(id) === parseFloat(id)) {
                firstKey = key;
            }
            if (value[firstKey]) {
                newObj[key] = value[key];
            }
        });
        const arr = Object.keys(newObj).map((key, index) => {
            var id = key.substring(key.indexOf(".") + 1, key.length);
            return {
                id: parseFloat(id),
                key: key,
                value: newObj[key]
            }
        });
        arr.sort((itemA, itemB) => itemA.id - itemB.id)
        return {
            newObj,
            arr
        };
    }

    getUserNameLabel(Role: UserRolesEnum) {
        if (Role === UserRolesEnum.Subject) {
            return "Patienten-ID";
        }
        else if (Role === UserRolesEnum.SiteAdministrator) {
            return "Arzt-Id";
        } else {
            return "Username";
        }
    }
}
